import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import FAQSection, { faqData } from "./FAQSection";
import { SeoTags } from "../../components/Head/SeoTags";
import { fetchPricing } from "../../services/api";
import { detectCountry } from "../../utils/geo";
import PriceSkeleton from "../../components/Pricing/PriceSkeleton";
import { formatPrice, getPriceLocale } from "../../utils/priceFormatter";

const Pricing = () => {
  const { t, i18n } = useTranslation();
  const [pricingData, setPricingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [billingCycle, setBillingCycle] = useState("yearly");

  useEffect(() => {
    const loadPricing = async () => {
      try {
        setLoading(true);
        // Prefer the visitor's geolocated country for currency; the API falls
        // back to the Accept-Language header when no country is provided.
        const country = await detectCountry();
        const data = await fetchPricing(country);
        if (data.success) {
          setPricingData(data.data);
        } else {
          setError("Failed to load pricing data");
        }
      } catch (err) {
        setError("Failed to load pricing data");
        console.error("Error loading pricing:", err);
      } finally {
        setLoading(false);
      }
    };

    loadPricing();
  }, [i18n.language]);

  const getFreePlanPrice = () => {
    if (loading) return <PriceSkeleton />;
    if (error || !pricingData) return "$0";
    const locale = getPriceLocale(i18n.language);
    return formatPrice(
      pricingData.plans.FREE.price,
      pricingData.currency,
      locale
    );
  };

  const getProPlanPrice = () => {
    if (loading) return <PriceSkeleton />;
    if (error || !pricingData) return billingCycle === "yearly" ? "$28" : "$3";
    const proPlan = pricingData.plans.PRO;
    const locale = getPriceLocale(i18n.language);
    const amount =
      billingCycle === "yearly" ? proPlan.yearly.price : proPlan.monthly.price;
    return formatPrice(amount, pricingData.currency, locale);
  };

  // Effective monthly cost when billed yearly, for the savings note.
  const getProMonthlyEquivalent = () => {
    if (!pricingData) return null;
    const locale = getPriceLocale(i18n.language);
    return formatPrice(
      pricingData.plans.PRO.yearly.price / 12,
      pricingData.currency,
      locale
    );
  };

  const proDiscount = pricingData?.plans?.PRO?.yearly?.discount ?? 22;

  return (
    <>
      <SeoTags
        title={t("pricing.seo.title")}
        description={t("pricing.seo.description")}
        keywords={t("pricing.seo.keywords")}
      />

      {/* Organization Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "onBookmarks",
            url: "https://onbookmarks.com",
            logo: "https://onbookmarks.com/images/icons/logo-only.svg",
          }),
        }}
      />

      {/* Product Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "onBookmarks",
            applicationCategory: "ProductivityApplication",
            operatingSystem: "Web, iOS, Android",
            offers: [
              {
                "@type": "Offer",
                name: "Free Plan",
                price: "0",
                priceCurrency: "USD",
                description:
                  "Perfect for personal use with unlimited bookmarks and basic features",
              },
              {
                "@type": "Offer",
                name: "Pro Plan",
                price: pricingData
                  ? pricingData.plans.PRO.yearly.price.toString()
                  : "28",
                priceCurrency: pricingData ? pricingData.currency : "USD",
                priceSpecification: {
                  "@type": "UnitPriceSpecification",
                  price: pricingData
                    ? pricingData.plans.PRO.yearly.price.toString()
                    : "28",
                  priceCurrency: pricingData ? pricingData.currency : "USD",
                  unitText: "YEAR",
                },
                description:
                  "Advanced features including unlimited spaces, AI tagging, and priority support",
              },
            ],
            description:
              "Professional bookmark manager with AI-powered organization, encryption, and multi-device sync.",
          }),
        }}
      />

      {/* FAQ Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqData.slice(0, 6).map((faq) => ({
              "@type": "Question",
              name: t(faq.questionKey),
              acceptedAnswer: {
                "@type": "Answer",
                text: t(faq.answerKey),
              },
            })),
          }),
        }}
      />

      {/* Breadcrumb Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://onbookmarks.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Pricing",
                item: "https://onbookmarks.com/pricing/",
              },
            ],
          }),
        }}
      />

      {/* Hero Section */}
      <section className="pricing-hero pt-5 mb-4">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-8 col-lg-10 col-12">
              <div className="text-center d-flex flex-column gap-3">
                <div className="d-flex flex-column gap-3">
                  <h1 className="display-5 fw-bold">
                    {t("pricing.hero.title")}
                  </h1>
                  <p className="lead text-muted">
                    {t("pricing.hero.description")}
                  </p>
                </div>
                <div className="d-flex justify-content-center mt-2">
                  <div
                    className="btn-group"
                    role="group"
                    aria-label={t("pricing.hero.billing.monthly")}
                  >
                    <button
                      type="button"
                      className={`btn ${
                        billingCycle === "monthly"
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() => setBillingCycle("monthly")}
                    >
                      {t("pricing.hero.billing.monthly")}
                    </button>
                    <button
                      type="button"
                      className={`btn d-flex align-items-center gap-2 ${
                        billingCycle === "yearly"
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() => setBillingCycle("yearly")}
                    >
                      {t("pricing.hero.billing.yearly")}
                      <span className="badge badge-save fw-bold">
                        {t("pricing.hero.billing.saveBadge")}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="container pricing-section pb-5">
        <div className="row justify-content-center">
          <div className="col-lg-9 col-12">
            <div className="row g-4">
              {/* Free Plan */}
              <div className="col-md-6 col-12">
                <div className="card pricing-card shadow-sm mt-4 h-100">
                  <div className="d-flex flex-column h-100">
                    <div className="text-center mt-3 p-4 border-bottom">
                      <h3 className="h2 mb-2 fw-bold">
                        {t("pricing.plans.free.name")}
                      </h3>
                      <p className="text-muted">
                        {t("pricing.plans.free.description")}
                      </p>
                    </div>

                    <div className="text-center p-4 bg-light d-flex justify-content-center align-items-center">
                      <div className="display-5 fw-bold mb-0">
                        {getFreePlanPrice()}
                      </div>
                      <span className="ms-2 text-muted">
                        {t("pricing.plans.free.period")}
                      </span>
                    </div>

                    <div className="p-4 flex-grow-1">
                      <ul className="list-unstyled feature-list">
                        {t("pricing.plans.free.features", {
                          returnObjects: true,
                        }).map((feature, index) => (
                          <li
                            key={index}
                            className="d-flex align-items-start mb-3"
                          >
                            <i className="bi bi-check-circle-fill text-success me-2"></i>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="d-grid mx-4 mt-0 mb-2">
                      <a
                        href={`${process.env.REACT_APP_APP_URL}/register`}
                        className="btn btn-light py-3"
                      >
                        <strong>{t("pricing.plans.free.cta")}</strong>
                      </a>
                    </div>
                    <p className="text-center small text-muted mb-5">
                      <i className="bi bi-check-circle me-1"></i>
                      {t("pricing.trust.free")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Pro Plan */}
              <div className="col-md-6 col-12">
                <div className="card h-100 mt-4 pricing-card pricing-card-featured shadow border-primary border-2 position-relative">
                  <div className="popular-badge">
                    <i className="bi bi-star-fill me-1"></i>
                    {t("pricing.plans.pro.badge")}
                  </div>

                  <div className="d-flex flex-column h-100">
                    <div className="text-center p-4 mt-3 border-bottom">
                      <h3 className="h2 fw-bold mb-2">
                        {t("pricing.plans.pro.name")}
                      </h3>
                      <p className="text-muted">
                        {t("pricing.plans.pro.description")}
                      </p>
                    </div>

                    <div className="text-center p-4 bg-light">
                      <div className=" d-flex justify-content-center align-items-center">
                        <div className="display-5 fw-bold mb-0">
                          {getProPlanPrice()}
                        </div>
                        <span className="text-muted ms-2">
                          {billingCycle === "yearly"
                            ? t("pricing.plans.pro.yearly")
                            : t("pricing.plans.pro.monthly")}
                        </span>
                      </div>
                      {billingCycle === "yearly" &&
                        !loading &&
                        !error &&
                        pricingData && (
                          <div className="small text-success fw-semibold mt-1">
                            {t("pricing.plans.pro.yearlyNote", {
                              perMonth: getProMonthlyEquivalent(),
                              discount: proDiscount,
                            })}
                          </div>
                        )}
                    </div>

                    <div className="flex-grow-1 p-4">
                      <ul className="list-unstyled feature-list">
                        {t("pricing.plans.pro.features", {
                          returnObjects: true,
                        }).map((feature, index) => (
                          <li
                            key={index}
                            className={`d-flex align-items-start mb-3 ${
                              index === 0 ? "fw-bold text-primary" : ""
                            }`}
                          >
                            <i className="bi bi-check-circle-fill text-success me-2"></i>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="d-grid mx-4 mt-0 mb-2">
                      <a
                        href={`${process.env.REACT_APP_APP_URL}/register?plan=pro&billing=${billingCycle}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary py-3"
                      >
                        <strong>{t("pricing.plans.pro.cta")}</strong>
                      </a>
                    </div>
                    <p className="text-center small text-muted mb-5">
                      <i className="bi bi-shield-check me-1"></i>
                      {t("pricing.trust.pro")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="bg-light py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <h3 className="h2 fw-bold mb-3 text-center">
                {t("pricing.comparison.title")}
              </h3>
              <p className="lead text-muted text-center mb-0">
                {t("pricing.comparison.description")}
              </p>
              <div className="mt-4 table-responsive shadow-sm rounded">
                <table className="table pricing-compare mb-0">
                  <thead>
                    <tr>
                      <th scope="col" className="px-4 py-3">
                        {t("pricing.comparison.table.headers.features")}
                      </th>
                      <th scope="col" className="text-center py-3">
                        {t("pricing.comparison.table.headers.free")}
                      </th>
                      <th scope="col" className="text-center py-3">
                        {t("pricing.comparison.table.headers.pro")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-4 fw-medium py-3">
                        {t("pricing.comparison.table.rows.bookmarks.label")}
                      </td>
                      <td className="text-center py-3">
                        {t("pricing.comparison.table.rows.bookmarks.free")}
                      </td>
                      <td className="text-center py-3 d-flex align-items-center justify-content-center">
                        <i className="bi bi-infinity text-primary me-2"></i>
                        <span>
                          {t("pricing.comparison.table.rows.bookmarks.pro")}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 fw-medium py-3">
                        {t("pricing.comparison.table.rows.spaces.label")}
                      </td>
                      <td className="text-center py-3">
                        {t("pricing.comparison.table.rows.spaces.free")}
                      </td>
                      <td className="text-center py-3 d-flex align-items-center justify-content-center">
                        <i className="bi bi-infinity text-primary me-2"></i>
                        <span>
                          {t("pricing.comparison.table.rows.spaces.pro")}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 fw-medium py-3">
                        {t("pricing.comparison.table.rows.ai.label")}
                      </td>
                      <td className="text-center py-3">
                        {t("pricing.comparison.table.rows.ai.free")}
                      </td>
                      <td className="text-center py-3">
                        {t("pricing.comparison.table.rows.ai.pro")}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 fw-medium py-3">
                        {t("pricing.comparison.table.rows.search.label")}
                      </td>
                      <td className="text-center py-3">
                        {t("pricing.comparison.table.rows.search.free")}
                      </td>
                      <td className="text-center py-3">
                        {t("pricing.comparison.table.rows.search.pro")}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection />

      {/* CTA Section */}
      <section className="bg-purple text-white py-5">
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-8">
              <h4 className="h2 text-white mb-3">{t("pricing.cta.title")}</h4>
              <p className="lead mb-4">{t("pricing.cta.description")}</p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <a
                  href={`${process.env.REACT_APP_APP_URL}/register?plan=pro`}
                  className="btn btn-light px-4"
                >
                  <span>{t("pricing.cta.buttons.startPro")}</span>
                  <i className="bi bi-arrow-right ms-2"></i>
                </a>
                <a
                  href={`${process.env.REACT_APP_APP_URL}/register`}
                  className="btn btn-outline-light px-4"
                >
                  <span>{t("pricing.cta.buttons.startFree")}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Pricing;
